import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import {
  Loader2, RefreshCw, Plus, Trash2, Search, Star, MessageSquare, Store,
  Image as ImageIcon, HelpCircle, BarChart3, Info, Send, AlertTriangle,
  ExternalLink, CheckCircle2, X, Link2, Save,
} from 'lucide-react';

/**
 * GbpHq — Google Business Profile yönetim merkezi (iç kullanım)
 * Route: /hq/gbp (ProtectedRoute allowEmails — sidebar'da BİLEREK yok)
 *
 * Çoklu Google hesabı bağlanır, tüm lokasyonlar tek listede toplanır.
 * Yorum yanıtlama, lokasyon bilgisi düzenleme, post, foto ve Q&A buradan yönetilir.
 */

const TABS = [
  { key: 'reviews', label: 'Yorumlar', icon: MessageSquare },
  { key: 'info', label: 'Bilgiler', icon: Info },
  { key: 'posts', label: 'Postlar', icon: Send },
  { key: 'media', label: 'Fotoğraflar', icon: ImageIcon },
  { key: 'qanda', label: 'Soru-Cevap', icon: HelpCircle },
  { key: 'perf', label: 'Performans', icon: BarChart3 },
];

const locIdOf = (name) => String(name || '').split('/').pop();

/**
 * Axios hatasından gösterilebilir bir nesne çıkar. Yanıt gövdesi JSON değilse
 * (proxy/nginx HTML hata sayfası) mesaj kaybolup boş kutu görünmesin diye
 * status + axios mesajına düşeriz.
 */
const errOfResponse = (e) => {
  const d = e?.response?.data;
  if (d && typeof d === 'object') return d;
  return { message: `${e?.response?.status || ''} ${e?.message || 'Bilinmeyen hata'}`.trim() };
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/** Google'ın SERVICE_DISABLED hatasını eyleme dönüşür şekilde göster. */
function ApiError({ err }) {
  if (!err) return null;
  const disabled = err.reason === 'SERVICE_DISABLED';
  return (
    <div className={`rounded-xl border p-4 text-sm ${disabled ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-rose-50 border-rose-200 text-rose-900'}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="space-y-1">
          <div className="font-medium">{disabled ? 'Bu API projede kapalı' : (err.error || 'Hata')}</div>
          <div className="opacity-90">{err.hint || err.message}</div>
          {disabled && (
            <a
              href={`https://console.cloud.google.com/apis/library?project=690297606648`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium underline"
            >
              Google Cloud Console'da aç <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      ))}
    </span>
  );
}

export default function GbpHq() {
  const [connections, setConnections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('reviews');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [globalErr, setGlobalErr] = useState(null);

  const say = (msg, kind = 'ok') => { setToast({ msg, kind }); setTimeout(() => setToast(null), 4000); };
  const errOf = errOfResponse;

  // ── Yükleyiciler ───────────────────────────────────────────────────────────
  const loadConnections = useCallback(async () => {
    try {
      const { data } = await api.get('/gbp/connections');
      setConnections(data.connections || []);
    } catch (e) { setGlobalErr(errOf(e)); }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get('/gbp/locations', { params: q ? { q } : {} });
      setLocations(data.locations || []);
    } catch (e) { setGlobalErr(errOf(e)); }
  }, [q]);

  useEffect(() => { loadConnections(); }, [loadConnections]);
  useEffect(() => { const t = setTimeout(loadLocations, 250); return () => clearTimeout(t); }, [loadLocations]);

  // OAuth dönüşünü karşıla
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('success')) { say(`${p.get('success')} bağlandı`); window.history.replaceState({}, '', '/hq/gbp'); loadConnections(); }
    if (p.get('error')) { say(`Bağlantı hatası: ${p.get('error')}`, 'err'); window.history.replaceState({}, '', '/hq/gbp'); }
  }, [loadConnections]);

  // ── Eylemler ───────────────────────────────────────────────────────────────
  const connectGoogle = async () => {
    try {
      const { data } = await api.get('/gbp/oauth/url');
      window.location.href = data.url;
    } catch (e) { say('Onay URL alınamadı', 'err'); }
  };

  const disconnect = async (id, email) => {
    if (!window.confirm(`${email} bağlantısı ve bu hesaba ait önbellek/yorum kayıtları silinecek. Emin misiniz?`)) return;
    try {
      await api.delete(`/gbp/connections/${id}`);
      say('Bağlantı kaldırıldı');
      loadConnections(); loadLocations(); setSelected(null);
    } catch (e) { say('Silinemedi', 'err'); }
  };

  const syncLocations = async () => {
    setBusy('locations');
    try {
      const { data } = await api.post('/gbp/sync/locations');
      const total = (data.summary || []).reduce((a, s) => a + (s.locations || 0), 0);
      const errs = (data.summary || []).flatMap(s => s.errors || []);
      say(`${total} lokasyon güncellendi${errs.length ? ` (${errs.length} hata)` : ''}`, errs.length ? 'warn' : 'ok');
      if (errs.length) setGlobalErr(errs[0]);
      loadLocations(); loadConnections();
    } catch (e) { setGlobalErr(errOf(e)); } finally { setBusy(''); }
  };

  const syncReviews = async (locId) => {
    setBusy('reviews');
    try {
      const { data } = await api.post('/gbp/sync/reviews', locId ? { locId } : {});
      const failed = (data.summary || []).find(s => s.status);
      if (failed) { setGlobalErr(failed); say('Yorum senkronu başarısız', 'err'); }
      else {
        const n = (data.summary || []).reduce((a, s) => a + (s.reviews || 0), 0);
        say(`${n} yorum senkronlandı`); setGlobalErr(null);
      }
      loadLocations();
    } catch (e) { setGlobalErr(errOf(e)); } finally { setBusy(''); }
  };

  const totals = useMemo(() => ({
    locations: locations.length,
    unanswered: locations.reduce((a, l) => a + (l.unanswered_count || 0), 0),
    reviews: locations.reduce((a, l) => a + (l.review_count || 0), 0),
  }), [locations]);

  return (
    <div className="p-6 space-y-5 max-w-[1600px]">
      {/* Başlık */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Store className="w-6 h-6 text-blue-600" /> Google Business Profile
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            İç kullanım paneli · {totals.locations} lokasyon · {totals.reviews} yorum
            {totals.unanswered > 0 && <span className="text-rose-600 font-medium"> · {totals.unanswered} yanıtlanmamış</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncLocations} disabled={!!busy}
            className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy === 'locations' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Lokasyonları Tazele
          </button>
          <button onClick={() => syncReviews()} disabled={!!busy}
            className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy === 'reviews' ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            Yorumları Çek
          </button>
          <button onClick={connectGoogle}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Google Hesabı Bağla
          </button>
        </div>
      </div>

      {/* Bağlantılar */}
      <div className="flex flex-wrap gap-2">
        {connections.map(c => (
          <div key={c.id}
            className={`inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border text-sm ${
              c.status === 'ACTIVE' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}
            title={c.last_error || ''}>
            <Link2 className="w-3.5 h-3.5" />
            <span className="font-medium">{c.google_email}</span>
            <span className="opacity-70">{c.location_count} lokasyon</span>
            {c.status !== 'ACTIVE' && <span className="font-medium">· yeniden bağlanmalı</span>}
            <button onClick={() => disconnect(c.id, c.google_email)} className="p-1 rounded-full hover:bg-black/10">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {connections.length === 0 && (
          <div className="text-sm text-gray-500">Henüz Google hesabı bağlı değil — sağ üstten bağlayın.</div>
        )}
      </div>

      {globalErr && <ApiError err={globalErr} />}

      <div className="grid grid-cols-12 gap-5">
        {/* Lokasyon listesi */}
        <div className="col-span-12 lg:col-span-4 xl:col-span-3 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Lokasyon ara…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {locations.map(l => (
              <button key={l.location_name} onClick={() => setSelected(l)}
                className={`w-full text-left p-3 rounded-xl border transition ${
                  selected?.location_name === l.location_name ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-200' : 'bg-white hover:bg-gray-50'}`}>
                <div className="font-medium text-sm">{l.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{l.locality} {l.admin_area}</div>
                <div className="flex items-center gap-2 mt-2 text-xs">
                  {l.avg_rating ? <span className="inline-flex items-center gap-1 text-amber-700">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />{l.avg_rating}</span> : null}
                  <span className="text-gray-500">{l.review_count} yorum</span>
                  {l.unanswered_count > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-medium">
                      {l.unanswered_count} yanıtsız
                    </span>
                  )}
                </div>
              </button>
            ))}
            {locations.length === 0 && (
              <div className="text-sm text-gray-500 p-3 border rounded-xl bg-gray-50">
                Lokasyon yok. Hesap bağlayıp "Lokasyonları Tazele" deyin.
              </div>
            )}
          </div>
        </div>

        {/* Detay */}
        <div className="col-span-12 lg:col-span-8 xl:col-span-9">
          {!selected ? (
            <div className="border rounded-xl p-10 text-center text-gray-500 bg-gray-50">
              Soldan bir lokasyon seçin.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-lg font-semibold">{selected.title}</div>
                  <div className="text-sm text-gray-500">
                    {selected.address_line} · {selected.google_email}
                  </div>
                </div>
                {selected.maps_uri && (
                  <a href={selected.maps_uri} target="_blank" rel="noreferrer"
                    className="text-sm text-blue-600 inline-flex items-center gap-1">
                    Haritada gör <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>

              <div className="flex gap-1 border-b overflow-x-auto">
                {TABS.map(t => (
                  <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${
                      tab === t.key ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
                    <t.icon className="w-4 h-4" /> {t.label}
                  </button>
                ))}
              </div>

              {tab === 'reviews' && <ReviewsTab loc={selected} onSync={() => syncReviews(locIdOf(selected.location_name))} busy={busy} say={say} onChanged={loadLocations} />}
              {tab === 'info' && <InfoTab loc={selected} say={say} onSaved={loadLocations} />}
              {tab === 'posts' && <PostsTab loc={selected} say={say} />}
              {tab === 'media' && <MediaTab loc={selected} say={say} />}
              {tab === 'qanda' && <QandaTab loc={selected} say={say} />}
              {tab === 'perf' && <PerfTab loc={selected} />}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm text-white z-50 ${
          toast.kind === 'err' ? 'bg-rose-600' : toast.kind === 'warn' ? 'bg-amber-600' : 'bg-emerald-600'}`}>
          <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{toast.msg}</span>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Yorumlar
// ═══════════════════════════════════════════════════════════════════════════
function ReviewsTab({ loc, onSync, busy, say, onChanged }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);
  const [rating, setRating] = useState('');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState('');
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/gbp/reviews', {
        params: { locId: locIdOf(loc.location_name), ...(onlyUnanswered ? { unanswered: 1 } : {}), ...(rating ? { rating } : {}) },
      });
      setReviews(data.reviews || []); setStats(data.stats); setErr(null);
    } catch (e) { setErr(errOfResponse(e)); }
  }, [loc, onlyUnanswered, rating]);

  useEffect(() => { load(); }, [load]);

  const reply = async (rv) => {
    const comment = (drafts[rv.review_name] || '').trim();
    if (!comment) return;
    setSaving(rv.review_name);
    try {
      await api.put(`/gbp/reviews/${locIdOf(loc.location_name)}/${rv.review_id}/reply`, { comment });
      say('Yanıt gönderildi'); setDrafts(d => ({ ...d, [rv.review_name]: '' }));
      load(); onChanged?.();
    } catch (e) { setErr(errOfResponse(e)); say('Yanıt gönderilemedi', 'err'); }
    finally { setSaving(''); }
  };

  const removeReply = async (rv) => {
    if (!window.confirm('Bu yanıt Google\'dan silinecek. Emin misiniz?')) return;
    setSaving(rv.review_name);
    try {
      await api.delete(`/gbp/reviews/${locIdOf(loc.location_name)}/${rv.review_id}/reply`);
      say('Yanıt silindi'); load(); onChanged?.();
    } catch (e) { setErr(errOfResponse(e)); say('Silinemedi', 'err'); }
    finally { setSaving(''); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onSync} disabled={!!busy}
          className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50">
          {busy === 'reviews' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Bu lokasyonu senkronla
        </button>
        <label className="text-sm inline-flex items-center gap-1.5">
          <input type="checkbox" checked={onlyUnanswered} onChange={e => setOnlyUnanswered(e.target.checked)} />
          Sadece yanıtlanmamış
        </label>
        <select value={rating} onChange={e => setRating(e.target.value)} className="text-sm border rounded-lg px-2 py-1.5">
          <option value="">Tüm puanlar</option>
          {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} yıldız</option>)}
        </select>
        {stats && <span className="text-sm text-gray-500 ml-auto">
          Toplam {stats.total} · Ortalama {stats.avg_rating ?? '—'}
        </span>}
      </div>

      <ApiError err={err} />

      <div className="space-y-3">
        {reviews.map(rv => (
          <div key={rv.review_name} className="border rounded-xl p-4 bg-white">
            <div className="flex items-start gap-3">
              {rv.reviewer_photo
                ? <img src={rv.reviewer_photo} alt="" className="w-9 h-9 rounded-full" />
                : <div className="w-9 h-9 rounded-full bg-gray-200" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{rv.reviewer_name || 'Anonim'}</span>
                  <Stars value={rv.star_value} />
                  <span className="text-xs text-gray-400">{fmtDate(rv.create_time)}</span>
                  {!rv.reply_comment && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-medium">yanıtsız</span>
                  )}
                </div>
                {rv.comment && <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap">{rv.comment}</p>}

                {rv.reply_comment ? (
                  <div className="mt-3 pl-3 border-l-2 border-blue-200 bg-blue-50/50 rounded-r-lg p-2.5">
                    <div className="text-xs text-blue-700 font-medium mb-1">
                      İşletme yanıtı · {fmtDate(rv.reply_update_time)}
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{rv.reply_comment}</p>
                    <button onClick={() => removeReply(rv)} disabled={saving === rv.review_name}
                      className="mt-2 text-xs text-rose-600 hover:underline inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Yanıtı sil
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 flex items-start gap-2">
                    <textarea
                      value={drafts[rv.review_name] || ''}
                      onChange={e => setDrafts(d => ({ ...d, [rv.review_name]: e.target.value }))}
                      placeholder="Yanıt yazın…" rows={2}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm" />
                    <button onClick={() => reply(rv)} disabled={saving === rv.review_name || !(drafts[rv.review_name] || '').trim()}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
                      {saving === rv.review_name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Yanıtla
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {reviews.length === 0 && !err && (
          <div className="text-sm text-gray-500 p-4 border rounded-xl bg-gray-50">
            Yorum yok. "Bu lokasyonu senkronla" ile Google'dan çekin.
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bilgiler (düzenlenebilir)
// ═══════════════════════════════════════════════════════════════════════════
const EDITABLE = [
  { key: 'title', label: 'İşletme adı', path: d => d.title },
  { key: 'websiteUri', label: 'Web sitesi', path: d => d.websiteUri },
  { key: 'phoneNumbers', label: 'Telefon', path: d => d.phoneNumbers?.primaryPhone, wrap: v => ({ primaryPhone: v }) },
  { key: 'storeCode', label: 'Mağaza kodu', path: d => d.storeCode },
];

function InfoTab({ loc, say, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gbp/locations/${locIdOf(loc.location_name)}`);
      setDetail(data.location);
      setForm(Object.fromEntries(EDITABLE.map(f => [f.key, f.path(data.location) || ''])));
      setErr(null);
    } catch (e) { setErr(errOfResponse(e)); }
  }, [loc]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!detail) return [];
    return EDITABLE.filter(f => (form[f.key] || '') !== (f.path(detail) || ''));
  }, [form, detail]);

  const save = async () => {
    if (!dirty.length) return;
    if (!window.confirm(`${dirty.length} alan Google'daki canlı kayda yazılacak:\n\n${dirty.map(f => `• ${f.label}`).join('\n')}\n\nDevam?`)) return;
    setSaving(true);
    try {
      const fields = {};
      dirty.forEach(f => { fields[f.key] = f.wrap ? f.wrap(form[f.key]) : form[f.key]; });
      await api.patch(`/gbp/locations/${locIdOf(loc.location_name)}`, { fields });
      say('Google\'a yazıldı'); setErr(null); load(); onSaved?.();
    } catch (e) { setErr(errOfResponse(e)); say('Kaydedilemedi', 'err'); }
    finally { setSaving(false); }
  };

  if (err && !detail) return <ApiError err={err} />;
  if (!detail) return <div className="p-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <ApiError err={err} />
      <div className="rounded-xl border bg-amber-50 border-amber-200 text-amber-900 text-sm p-3 flex gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        Buradaki değişiklikler doğrudan Google'daki canlı işletme kaydına yazılır ve arama sonuçlarında görünür.
      </div>
      {EDITABLE.map(f => (
        <div key={f.key}>
          <label className="text-sm font-medium text-gray-700">{f.label}</label>
          <input value={form[f.key] || ''} onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value }))}
            className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${
              dirty.some(d => d.key === f.key) ? 'border-blue-400 bg-blue-50' : ''}`} />
        </div>
      ))}
      <div className="text-sm text-gray-500 space-y-1">
        <div><span className="font-medium">Kategori:</span> {detail.categories?.primaryCategory?.displayName || '—'}</div>
        <div><span className="font-medium">Adres:</span> {(detail.storefrontAddress?.addressLines || []).join(' ')} {detail.storefrontAddress?.locality}</div>
        <div><span className="font-medium">Durum:</span> {detail.openInfo?.status || '—'}</div>
      </div>
      <button onClick={save} disabled={!dirty.length || saving}
        className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm inline-flex items-center gap-2 disabled:opacity-40">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {dirty.length ? `${dirty.length} alanı Google'a yaz` : 'Değişiklik yok'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Postlar
// ═══════════════════════════════════════════════════════════════════════════
function PostsTab({ loc, say }) {
  const [posts, setPosts] = useState([]);
  const [err, setErr] = useState(null);
  const [summary, setSummary] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gbp/locations/${locIdOf(loc.location_name)}/posts`);
      setPosts(data.posts || []); setErr(null);
    } catch (e) { setErr(errOfResponse(e)); }
  }, [loc]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (!summary.trim()) return;
    setBusy(true);
    try {
      const body = { summary: summary.trim(), topicType: 'STANDARD', languageCode: 'tr' };
      if (ctaUrl.trim()) body.callToAction = { actionType: 'LEARN_MORE', url: ctaUrl.trim() };
      if (imageUrl.trim()) body.media = [{ mediaFormat: 'PHOTO', sourceUrl: imageUrl.trim() }];
      await api.post(`/gbp/locations/${locIdOf(loc.location_name)}/posts`, body);
      say('Post yayınlandı'); setSummary(''); setCtaUrl(''); setImageUrl(''); load();
    } catch (e) { setErr(errOfResponse(e)); say('Yayınlanamadı', 'err'); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm('Bu post Google\'dan silinecek. Emin misiniz?')) return;
    try {
      await api.delete(`/gbp/locations/${locIdOf(loc.location_name)}/posts/${String(p.name).split('/').pop()}`);
      say('Post silindi'); load();
    } catch (e) { setErr(errOfResponse(e)); }
  };

  return (
    <div className="space-y-4">
      <ApiError err={err} />
      <div className="border rounded-xl p-4 bg-white space-y-3 max-w-2xl">
        <div className="font-medium text-sm">Yeni post</div>
        <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={3}
          placeholder="Post metni (1500 karaktere kadar)…" className="w-full border rounded-lg px-3 py-2 text-sm" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="Buton linki (opsiyonel)"
            className="border rounded-lg px-3 py-2 text-sm" />
          <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="Görsel URL (opsiyonel, herkese açık)"
            className="border rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={publish} disabled={busy || !summary.trim()}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Yayınla
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {posts.map(p => (
          <div key={p.name} className="border rounded-xl p-3 bg-white space-y-2">
            {p.media?.[0]?.googleUrl && <img src={p.media[0].googleUrl} alt="" className="w-full h-32 object-cover rounded-lg" />}
            <div className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-4">{p.summary}</div>
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{fmtDate(p.createTime)} · {p.state}</span>
              <button onClick={() => remove(p)} className="text-rose-600 hover:underline inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Sil
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && !err && <div className="text-sm text-gray-500">Post yok.</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Fotoğraflar
// ═══════════════════════════════════════════════════════════════════════════
function MediaTab({ loc, say }) {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('ADDITIONAL');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gbp/locations/${locIdOf(loc.location_name)}/media`);
      setItems(data.mediaItems || []); setErr(null);
    } catch (e) { setErr(errOfResponse(e)); }
  }, [loc]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post(`/gbp/locations/${locIdOf(loc.location_name)}/media`, { sourceUrl: url.trim(), category });
      say('Fotoğraf yüklendi'); setUrl(''); load();
    } catch (e) { setErr(errOfResponse(e)); say('Yüklenemedi', 'err'); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm('Bu fotoğraf Google\'dan silinecek. Emin misiniz?')) return;
    try {
      await api.delete(`/gbp/locations/${locIdOf(loc.location_name)}/media/${String(m.name).split('/').pop()}`);
      say('Silindi'); load();
    } catch (e) { setErr(errOfResponse(e)); }
  };

  return (
    <div className="space-y-4">
      <ApiError err={err} />
      <div className="flex gap-2 flex-wrap items-center max-w-3xl">
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="Herkese açık görsel URL'i"
          className="flex-1 min-w-[240px] border rounded-lg px-3 py-2 text-sm" />
        <select value={category} onChange={e => setCategory(e.target.value)} className="border rounded-lg px-2 py-2 text-sm">
          {['ADDITIONAL', 'COVER', 'PROFILE', 'LOGO', 'EXTERIOR', 'INTERIOR', 'PRODUCT', 'AT_WORK', 'TEAMS'].map(c =>
            <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={upload} disabled={busy || !url.trim()}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Yükle
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {items.map(m => (
          <div key={m.name} className="border rounded-xl overflow-hidden bg-white group relative">
            <img src={m.thumbnailUrl || m.googleUrl} alt="" className="w-full h-28 object-cover" />
            <div className="p-2 text-xs text-gray-500 truncate">{m.locationAssociation?.category || m.mediaFormat}</div>
            <button onClick={() => remove(m)}
              className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-white/90 text-rose-600 opacity-0 group-hover:opacity-100 transition">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {items.length === 0 && !err && <div className="text-sm text-gray-500">Fotoğraf yok.</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Soru-Cevap
// ═══════════════════════════════════════════════════════════════════════════
function QandaTab({ loc, say }) {
  const [questions, setQuestions] = useState([]);
  const [err, setErr] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gbp/locations/${locIdOf(loc.location_name)}/questions`);
      setQuestions(data.questions || []); setErr(null);
    } catch (e) { setErr(errOfResponse(e)); }
  }, [loc]);
  useEffect(() => { load(); }, [load]);

  const answer = async (qs) => {
    const text = (drafts[qs.name] || '').trim();
    if (!text) return;
    setBusy(qs.name);
    try {
      await api.post(`/gbp/locations/${locIdOf(loc.location_name)}/questions/${String(qs.name).split('/').pop()}/answer`, { text });
      say('Cevap gönderildi'); setDrafts(d => ({ ...d, [qs.name]: '' })); load();
    } catch (e) { setErr(errOfResponse(e)); say('Gönderilemedi', 'err'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-3">
      <ApiError err={err} />
      {questions.map(qs => (
        <div key={qs.name} className="border rounded-xl p-4 bg-white">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{qs.author?.displayName || 'Kullanıcı'}</span>
            <span className="text-xs text-gray-400">{fmtDate(qs.createTime)}</span>
            {qs.upvoteCount > 0 && <span className="text-xs text-gray-500">↑{qs.upvoteCount}</span>}
          </div>
          <p className="text-sm text-gray-700 mt-1">{qs.text}</p>
          {(qs.topAnswers || []).map(a => (
            <div key={a.name} className="mt-2 pl-3 border-l-2 border-gray-200 text-sm text-gray-600">
              <span className="text-xs text-gray-400">{a.author?.displayName} · {a.author?.type}</span>
              <div>{a.text}</div>
            </div>
          ))}
          <div className="mt-3 flex items-start gap-2">
            <textarea value={drafts[qs.name] || ''} onChange={e => setDrafts(d => ({ ...d, [qs.name]: e.target.value }))}
              rows={2} placeholder="İşletme olarak cevapla…" className="flex-1 border rounded-lg px-3 py-2 text-sm" />
            <button onClick={() => answer(qs)} disabled={busy === qs.name || !(drafts[qs.name] || '').trim()}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
              {busy === qs.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Cevapla
            </button>
          </div>
        </div>
      ))}
      {questions.length === 0 && !err && <div className="text-sm text-gray-500">Soru yok.</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Performans
// ═══════════════════════════════════════════════════════════════════════════
const METRIC_LABELS = {
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: 'Arama (masaüstü)',
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: 'Arama (mobil)',
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: 'Haritalar (masaüstü)',
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: 'Haritalar (mobil)',
  CALL_CLICKS: 'Telefon tıklaması',
  WEBSITE_CLICKS: 'Web sitesi tıklaması',
  BUSINESS_DIRECTION_REQUESTS: 'Yol tarifi',
  BUSINESS_CONVERSATIONS: 'Mesaj',
  BUSINESS_BOOKINGS: 'Rezervasyon',
};

function PerfTab({ loc }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/gbp/locations/${locIdOf(loc.location_name)}/performance`);
        setData(d); setErr(null);
      } catch (e) { setErr(errOfResponse(e)); }
    })();
  }, [loc]);

  const rows = useMemo(() => {
    const series = data?.multiDailyMetricTimeSeries || [];
    const out = [];
    series.forEach(s => (s.dailyMetricTimeSeries || []).forEach(m => {
      const total = (m.timeSeries?.datedValues || []).reduce((a, v) => a + Number(v.value || 0), 0);
      out.push({ metric: m.dailyMetric, total });
    }));
    return out.sort((a, b) => b.total - a.total);
  }, [data]);

  if (err) return <ApiError err={err} />;
  if (!data) return <div className="p-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500">Son 30 gün toplamları</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {rows.map(r => (
          <div key={r.metric} className="border rounded-xl p-4 bg-white">
            <div className="text-xs text-gray-500">{METRIC_LABELS[r.metric] || r.metric}</div>
            <div className="text-2xl font-semibold mt-1">{r.total.toLocaleString('tr-TR')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
